export type TipCategory = "saving" | "spending" | "investing" | "parent";

export interface Tip {
  emoji: string;
  tip: string;
  category: TipCategory;
}

export const TIPS: Tip[] = [
  // ── Saving (8) ──────────────────────────────────────────────────────────
  { emoji: "🐷", tip: "Save at least 10% of every dollar you get — even tiny amounts add up fast!", category: "saving" },
  { emoji: "💰", tip: "Pay yourself first: put money into savings before you spend a single cent.", category: "saving" },
  { emoji: "🏦", tip: "A savings account earns interest while you sleep — your money works for you!", category: "saving" },
  { emoji: "🎯", tip: "Having a savings goal makes it WAY easier to say no to things you don't really need.", category: "saving" },
  { emoji: "📦", tip: "The 24-hour rule: wait a full day before any non-essential purchase. Still want it? Then buy it.", category: "saving" },
  { emoji: "🐢", tip: "Slow and steady wins the race. Saving $5 a week adds up to $260 a year!", category: "saving" },
  { emoji: "💎", tip: "Your nest egg is your future self's best friend. Protect it like it's treasure — because it is.", category: "saving" },
  { emoji: "🌱", tip: "Starting young is a superpower. Even saving $1 a week builds the habit that matters most.", category: "saving" },

  // ── Spending (7) ─────────────────────────────────────────────────────────
  { emoji: "🛒", tip: "Make a list before you shop so you only buy what you actually planned to buy.", category: "spending" },
  { emoji: "🤔", tip: "Before every purchase ask yourself: 'Do I NEED this, or do I just WANT it right now?'", category: "spending" },
  { emoji: "🏷️", tip: "Always compare prices before buying — the same item can cost way less somewhere else.", category: "spending" },
  { emoji: "🎮", tip: "Fun doesn't have to be expensive. Some of the best times cost nothing at all.", category: "spending" },
  { emoji: "💸", tip: "Small daily purchases add up fast. Spending $3 on a snack every day = $1,095 a year!", category: "spending" },
  { emoji: "🧾", tip: "Keeping track of what you spend shows you where your money REALLY goes.", category: "spending" },
  { emoji: "🎁", tip: "Homemade gifts are often more meaningful than store-bought ones — and they cost less.", category: "spending" },

  // ── Investing (8) ────────────────────────────────────────────────────────
  { emoji: "📈", tip: "Compound interest means your money earns money on itself. Over time, it snowballs!", category: "investing" },
  { emoji: "⏰", tip: "Time is the most powerful ingredient in investing. Starting early beats starting big.", category: "investing" },
  { emoji: "🌍", tip: "Buying a stock means owning a tiny piece of a real company. You grow when they grow.", category: "investing" },
  { emoji: "🎲", tip: "Investing has some risk — but NOT investing has the risk of missing out on decades of growth.", category: "investing" },
  { emoji: "📊", tip: "Diversify: spread your money across different things so one bad day doesn't wipe you out.", category: "investing" },
  { emoji: "🚀", tip: "At 7% average annual growth, money roughly doubles every 10 years. Start now!", category: "investing" },
  { emoji: "🧮", tip: "Rule of 72: divide 72 by your interest rate to find out how many years to double your money.", category: "investing" },
  { emoji: "💡", tip: "A Roth IRA lets your money grow completely tax-free. Ask a parent about opening one!", category: "investing" },

  // ── Parent prompts (7) ───────────────────────────────────────────────────
  { emoji: "👨‍👩‍👧", tip: "Parent prompt: Ask 'How do you decide what's worth spending money on vs. saving for later?'", category: "parent" },
  { emoji: "🏠", tip: "Parent prompt: Ask 'What's the biggest money lesson you wish you'd learned at my age?'", category: "parent" },
  { emoji: "💳", tip: "Parent prompt: Ask 'How does a credit card work, and why do people use them?'", category: "parent" },
  { emoji: "🎓", tip: "Parent prompt: Ask 'What is a 401(k) and why do grown-ups care so much about it?'", category: "parent" },
  { emoji: "🛡️", tip: "Parent prompt: Ask 'What is insurance, and why do we pay for something we hope to never use?'", category: "parent" },
  { emoji: "🏦", tip: "Parent prompt: Ask 'Can we visit a bank together so I can see how it actually works?'", category: "parent" },
  { emoji: "💼", tip: "Parent prompt: Ask 'What was your first job, and what's the most important thing you learned from it?'", category: "parent" },
];

const CAT_COLORS: Record<TipCategory, { bg: string; border: string; text: string; tag: string; tagText: string }> = {
  saving:    { bg: "#fffbeb", border: "#fbbf24", text: "#92400e", tag: "#fef3c7", tagText: "#b45309" },
  spending:  { bg: "#eff6ff", border: "#93c5fd", text: "#1e40af", tag: "#dbeafe", tagText: "#1d4ed8" },
  investing: { bg: "#fdf4ff", border: "#d8b4fe", text: "#6b21a8", tag: "#f3e8ff", tagText: "#7e22ce" },
  parent:    { bg: "#fff7ed", border: "#fb923c", text: "#9a3412", tag: "#ffedd5", tagText: "#c2410c" },
};

export const CAT_LABELS: Record<TipCategory, string> = {
  saving:    "💰 Saving",
  spending:  "🛒 Spending",
  investing: "📈 Investing",
  parent:    "👨‍👩‍👧 Ask a Parent",
};

export function getTodaysTip(): Tip {
  const dayIndex = Math.floor(Date.now() / 86400000);
  return TIPS[dayIndex % TIPS.length];
}

export function getTipColors(category: TipCategory) {
  return CAT_COLORS[category];
}
